import { t } from "@redotech/redo-i18n";
import { OrderTrackingShipmentStatus } from "@redotech/redo-model/order-tracking/order-tracking-types";
import type { TrackingPageShipment } from "@redotech/redo-model/order-tracking/tracking-page-order-definition";
import { memo } from "react";
import { RedoBadge } from "../../arbiter-components/badge/redo-badge";
import { Header, Text } from "../../text";
import type { PickupConfig } from "./pick-up-config-util";

export enum ShipmentStatusDisplayType {
  HEADER = "header",
  TEXT = "text",
  BADGE = "badge",
}

export const ShipmentStatusDisplay = memo(function ShipmentStatusDisplay({
  shipment,
  cancelReason,
  source,
  pickupConfig,
  displayType,
  storeUrl,
  stalledDays,
  headerStyles,
}: {
  shipment?: TrackingPageShipment;
  cancelReason?: string | null;
  source?: string | null;
  pickupConfig: PickupConfig;
  displayType?: ShipmentStatusDisplayType;
  storeUrl: string;
  stalledDays: number | null;
  headerStyles?: React.CSSProperties;
}) {
  const customMessage = getCustomOrderStatusMessage({ storeUrl, shipment });
  if (customMessage) {
    return (
      <Text fontSize="xl" fontWeight="bold">
        {customMessage}
      </Text>
    );
  }

  if (cancelReason) {
    return (
      <Text fontSize="xl" fontWeight="bold">
        {t(
          "Your order has been cancelled, please contact customer support for inquiries",
        )}
      </Text>
    );
  }

  const statusMessage = getStatusMessage({
    shipment,
    source,
    pickupConfig,
    stalledDays,
  });

  return displayType === ShipmentStatusDisplayType.TEXT ? (
    statusMessage
  ) : displayType === ShipmentStatusDisplayType.HEADER ? (
    <Header
      fontSize="sm"
      fontWeight="medium"
      mb="md"
      mt="md"
      style={headerStyles}
      textColor="secondary"
    >
      {statusMessage}
    </Header>
  ) : displayType === ShipmentStatusDisplayType.BADGE ? (
    <RedoBadge color="blue" size="md" text={statusMessage} />
  ) : (
    <Text fontSize="xl" fontWeight="bold" style={headerStyles}>
      {statusMessage}
    </Text>
  );
});

const shipmentStatusDisplayStrings: Record<
  OrderTrackingShipmentStatus,
  string
> = {
  [OrderTrackingShipmentStatus.PRE_TRANSIT]: t("Pre-transit"),
  [OrderTrackingShipmentStatus.PENDING]: t("Pending"),
  [OrderTrackingShipmentStatus.IN_TRANSIT]: t("In transit"),
  [OrderTrackingShipmentStatus.OUT_FOR_DELIVERY]: t("Out for delivery"),
  [OrderTrackingShipmentStatus.DELIVERED]: t("Delivered"),
  [OrderTrackingShipmentStatus.AVAILABLE_FOR_PICKUP]: t("Available for pickup"),
  [OrderTrackingShipmentStatus.AVAILABLE_FOR_PICKUP_CARRIER]: t(
    "Available for pickup",
  ),
  [OrderTrackingShipmentStatus.STALLED_IN_TRANSIT]: t("Stalled in transit"),
  [OrderTrackingShipmentStatus.RETURN_TO_SENDER]: t("Return to sender"),
  [OrderTrackingShipmentStatus.DELIVERY_ATTEMPTED]: t("Delivery attempted"),
  [OrderTrackingShipmentStatus.DELIVERY_FAILURE]: t("Delivery failure"),
  [OrderTrackingShipmentStatus.SHIPMENT_CANCELLED]: t("Cancelled"),
  [OrderTrackingShipmentStatus.SHIPMENT_ERROR]: t("Error"),
  [OrderTrackingShipmentStatus.UNKNOWN]: t("Pre-transit"),
};

function isStalledInTransit(
  shipment: TrackingPageShipment,
  stalledDays: number | null,
): boolean {
  if (stalledDays === null) {
    return false;
  }
  const updatedAt = shipment.updatedAt;
  if (!updatedAt) {
    return false;
  }
  const daysSinceUpdate =
    (Date.now() - new Date(updatedAt).getTime()) / (1000 * 60 * 60 * 24);
  return daysSinceUpdate >= stalledDays;
}

function getStatusMessage({
  shipment,
  source,
  pickupConfig,
  stalledDays,
}: {
  shipment?: TrackingPageShipment;
  source?: string | null;
  pickupConfig: PickupConfig;
  stalledDays: number | null;
}): string {
  const { isPickUpOrder, readyForPickUp, hasBeenPickedUp } = pickupConfig;
  if (isPickUpOrder && readyForPickUp) {
    return t("Ready for pickup");
  }
  if (isPickUpOrder && hasBeenPickedUp) {
    return t("Picked up");
  }

  if (!shipment) {
    if (source === "pos") {
      return t("Purchased in Store");
    }
    return t("Your order is being prepared");
  }
  if (
    shipment.status === OrderTrackingShipmentStatus.STALLED_IN_TRANSIT ||
    (shipment.status === OrderTrackingShipmentStatus.IN_TRANSIT &&
      isStalledInTransit(shipment, stalledDays))
  ) {
    return t("Stalled in transit");
  }
  return shipmentStatusDisplayStrings[shipment.status];
}

function getCustomOrderStatusMessage({
  storeUrl,
  shipment,
}: {
  storeUrl: string;
  shipment?: TrackingPageShipment;
}): string | undefined {
  const statusMapping: Record<string, () => string | undefined> = {
    "andar-wallets.myshopify.com": () => {
      if (shipment && shipment.carrier === "Self-Pickup (by PDQ)") {
        return "Ready for Pickup";
      }
      return undefined;
    },
    "waterfield-designs.myshopify.com": () => {
      if (!shipment) {
        return "In sewing queue";
      }
      return undefined;
    },
    "lindsey-leigh-jewelry.myshopify.com": () => {
      if (!shipment) {
        return "Your order is being prepared";
      }
      return undefined;
    },
  };
  return statusMapping[storeUrl]?.();
}
